// MARK://dhk8/admin-shell/pages/LoginPage
// Auth surface for the merchant admin: PIN (4-digit id + 6-digit clave) OR email/password
// against dhk8-platform-auth. On success the auth store persists tenantId/userId/roles and
// we redirect to ?returnTo (default /dashboard). With VITE_DEV_LOGIN=1 a "modo demo" link
// seeds a fake owner+superadmin session so the no-backend preview works.
import { useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useAuthStore } from '../stores/auth-store.js'
import { DEV_LOGIN } from '../lib/env.js'
import BrandMark from '../components/BrandMark.js'
import ThemeSwitcher from '../components/ThemeSwitcher.js'
import { t } from '../i18n/es-MX.js'

export default function LoginPage() {
  const nav = useNavigate()
  const [params] = useSearchParams()
  const returnTo = params.get('returnTo') ? decodeURIComponent(params.get('returnTo')!) : '/dashboard'
  const { pinLogin, emailLogin, devLogin, loading, error } = useAuthStore()
  // admin.nintaigo.com es multi-tenant: el login por PIN no puede resolver el tenant sin
  // más contexto, así que correo/contraseña es el camino por defecto. El PIN del comerciante
  // es para el POS (que sí conoce su tenant por config del dispositivo).
  const [mode, setMode] = useState<'pin' | 'email'>('email')
  const [pinId, setPinId] = useState('')
  const [pinPassword, setPinPassword] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    try {
      if (mode === 'pin') await pinLogin(pinId, pinPassword)
      else await emailLogin(email, password)
      nav(returnTo, { replace: true })
    } catch { /* error already in store */ }
  }
  function doDevLogin() { devLogin(); nav(returnTo, { replace: true }) }

  return (
    <div className="adm-auth">
      <div style={{ position: 'absolute', top: 12, right: 12 }}><ThemeSwitcher /></div>
      <div className="adm-auth__card adm-card">
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}><BrandMark size="lg" tagline="Admin" /></div>
        <h1 className="adm-page__title" style={{ textAlign: 'center', fontSize: '1.25rem' }}>{t('auth.title')}</h1>

        <div className="adm-auth__tabs" role="tablist">
          <button type="button" role="tab" aria-selected={mode === 'pin'} className={`adm-btn adm-btn--sm${mode === 'pin' ? ' adm-btn--primary' : ''}`} onClick={() => setMode('pin')}>{t('auth.pin')}</button>
          <button type="button" role="tab" aria-selected={mode === 'email'} className={`adm-btn adm-btn--sm${mode === 'email' ? ' adm-btn--primary' : ''}`} onClick={() => setMode('email')}>{t('auth.email')}</button>
        </div>

        <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
          {mode === 'pin' ? (
            <>
              <label className="adm-field"><span className="adm-field__label">{t('auth.pinId')}</span>
                <input className="adm-input" inputMode="numeric" maxLength={6} value={pinId} onChange={(e) => setPinId(e.target.value.replace(/\D/g, ''))} placeholder="100042" autoFocus /></label>
              <label className="adm-field"><span className="adm-field__label">{t('auth.pinPassword')}</span>
                <input className="adm-input" type="password" inputMode="numeric" maxLength={6} value={pinPassword} onChange={(e) => setPinPassword(e.target.value.replace(/\D/g, ''))} placeholder="••••••" /></label>
            </>
          ) : (
            <>
              <label className="adm-field"><span className="adm-field__label">{t('auth.emailLabel')}</span>
                <input className="adm-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="dueno@negocio.mx" autoFocus /></label>
              <label className="adm-field"><span className="adm-field__label">{t('auth.passwordLabel')}</span>
                <input className="adm-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} /></label>
            </>
          )}
          {error && <div className="adm-banner adm-banner--danger" role="alert">{error}</div>}
          <button type="submit" className="adm-btn adm-btn--primary" disabled={loading}>{loading ? '…' : t('auth.enter')}</button>
        </form>

        {DEV_LOGIN && (
          <button type="button" className="adm-btn adm-btn--ghost adm-btn--sm" style={{ marginTop: 10, width: '100%' }} onClick={doDevLogin}>{t('auth.devLogin')}</button>
        )}
        <div style={{ textAlign: 'center', marginTop: 14, fontSize: 13 }}>
          ¿Negocio nuevo? <Link to="/signup" style={{ color: 'var(--pos-tint)', fontWeight: 600 }}>Crear cuenta</Link>
        </div>
      </div>
      <style>{styles}</style>
    </div>
  )
}

const styles = `
  .adm-auth { min-height: 100dvh; display: flex; align-items: center; justify-content: center; padding: var(--pos-space-4); background: var(--pos-canvas); position: relative; }
  .adm-auth__card { width: min(420px, 100%); }
  .adm-auth__tabs { display: flex; gap: var(--pos-space-2); margin-top: var(--pos-space-3); }
  .adm-auth__tabs > * { flex: 1; }
`
